// src/pages/Accounting/AddMapAccountCode/AddMapAccountCode.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import { fetchDataRead } from "../../../Utility"; // same helper you already use

const AddMapAccountCode = () => {
  const routeLocation = useLocation();

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const [editId, setEditId] = useState(null);

  // lookups
  const [codeTypeList, setCodeTypeList] = useState([]); // /codetypes/
  const [hsnList, setHsnList] = useState([]); // /hsnmaster/
  const [gstTypeList, setGstTypeList] = useState([]); // /gsttypes/
  const [accountList, setAccountList] = useState([]); // /accountcodes/
  const [codeTypes, setCodeTypes] = useState([]);
  const [gstTypes, setGstTypes] = useState([]);
  const [groupaccount, setGroupaccount] = useState([]);
  const [gstReceivableSlOptions, setGstReceivableSlOptions] = useState([]);
  const [gstPayableSlOptions, setGstPayableSlOptions] = useState([]);

  const [rcmReceivableSlOptions, setRcmReceivableSlOptions] = useState([]);
  const [rcmPayableSlOptions, setRcmPayableSlOptions] = useState([]);

  const [gstProvisionalReceivableSlOptions, setGstProvisionalReceivableSlOptions] = useState([]);

  const [itcNoSlOptions, setItcNoSlOptions] = useState([]);

  // form state — mirrors screenshot fields
  const [form, setForm] = useState({
    code_type_id: "", // 👈 INT (for SAVE)
    code_type: "", // 👈 STRING (for HSN API)
    hsn_sac_code: "",
    gst_type: "",
    effective_date: "",

    gst_receivable_gl: "",
    gst_receivable_sl: "",
    gst_payable_gl: "",
    gst_payable_sl: "",

    rcm_receivable_gl: "",
    rcm_receivable_sl: "",
    rcm_payable_gl: "",
    rcm_payable_sl: "",

    gst_provisional_receivable_gl: "",
    gst_provisional_receivable_sl: "",

    itc_no_gl: "",
    itc_no_sl: "",
  });

  // ---------- styles & helpers (identical to AddTDSMaster, with better scroll) ----------
  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  const selectStyles = {
    control: (base) => ({ ...base, minHeight: 38, boxShadow: "none" }),
    valueContainer: (base) => ({
      ...base,
      paddingTop: 2,
      paddingBottom: 2,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      maxHeight: 250,
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: 240,
    }),
  };

  const accountSelectOptions = useMemo(
    () =>
      (accountList || []).map((o) => ({
        value: o.account_code,
        label: `${o.account_code}${o.account_name ? " - " + o.account_name : ""}`,
        _code: (o.account_code || "").toLowerCase(),
        _name: (o.account_name || "").toLowerCase(),
      })),
    [accountList]
  );

  const fetchCodeTypes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/GetCodeType/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      setCodeTypes(resp.data || []);
    } catch (e) {
      console.error("Error fetching code types:", e);
      toast.error("Failed to load code types");
    }
  };

  const fetchGstTypes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/GetGSTType/");
      const data = Array.isArray(resp.data) ? resp.data : [];
      setGstTypeList(data); // ✅ bind to correct state
    } catch (e) {
      console.error(e);
      toast.error("Failed to load GST Types");
    }
  };

  const fetchGroupaccount = async () => {
    try {
      const resp = await axios.get(baseUrl + "/getgroupaccount/");
      const data = Array.isArray(resp.data?.group_accounts)
        ? resp.data.group_accounts
        : [];

      setGroupaccount(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load Group Accounts");
    }
  };

  const fetchSubAccountsByGL = async (accountMasterId, setSlOptions) => {
    if (!accountMasterId) {
      setSlOptions([]);
      return;
    }

    try {
      const resp = await axios.post(
        `${baseUrl}/get_subaccountsbyaccount_master_id/`,
        { account_master_id: accountMasterId },
        { headers: { "Content-Type": "application/json" } }
      );

      const data = Array.isArray(resp.data) ? resp.data : [];

      setSlOptions(
        data.map((x) => ({
          value: x.sub_account_master_id,
          label: x.sub_account_name,
        }))
      );
    } catch (error) {
      console.error(error);
      // toast.error("Failed to load Sub Accounts");
      setSlOptions([]);
    }
  };

  const fetchHsnByCodeType = async (codeTypeName) => {
    if (!codeTypeName) {
      setHsnList([]);
      return;
    }

    const resp = await axios.post(`${baseUrl}/get_hsn_sac_bycodetype/`, {
      code_type: codeTypeName, // ✅ "HSN" / "SAC"
    });

    setHsnList(Array.isArray(resp.data) ? resp.data : []);
  };

  useEffect(() => {
    fetchCodeTypes();
    fetchGstTypes();
    fetchGroupaccount();
    fetchHsnByCodeType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const codeTypeOptions = useMemo(
    () =>
      (codeTypes || []).map((x) => ({
        value: x.code_type_id, // ✅ INTEGER for DB
        label: x.code_type_name, // visible text
        name: x.code_type_name, // ✅ STRING for HSN API
      })),
    [codeTypes]
  );

  const hsnOptions = useMemo(
    () =>
      (hsnList || []).map((o) => ({
        value: o.hsn_sac_master_id, // ✅ ID goes to DB
        label: o.hsn_sac_code, // ✅ visible code
      })),
    [hsnList]
  );

  const gstTypeOptions = useMemo(
    () =>
      (gstTypeList || []).map((x) => ({
        value: x.gst_type_id, // backend ID
        label: x.gst_type_name, // visible text
      })),
    [gstTypeList]
  );

  const groupaccountOptions = useMemo(
    () =>
      (groupaccount || []).map((x) => ({
        value: x.account_master_id, // backend ID
        label: x.name_on_account, // visible text
      })),
    [groupaccount]
  );

  const accountFilter = (option, rawInput) => {
    const q = rawInput.trim().toLowerCase();
    if (!q) return true;
    return (
      option.label.toLowerCase().includes(q) ||
      (option.data?._code || "").includes(q) ||
      (option.data?._name || "").includes(q)
    );
  };

  // edit mode via route state (keeps identical UX pattern)
  useEffect(() => {
    const d = routeLocation.state?.mapData;
    if (!d) return;

    setEditId(d.account_code_mapping_id);

    // ✅ preload HSN dropdown based on Code Type name
    fetchHsnByCodeType(d.code_type_name);

    // ✅ preload all SL dropdowns
    fetchSubAccountsByGL(d.gst_recievable_gl, setGstReceivableSlOptions);
    fetchSubAccountsByGL(d.gst_payable_gl, setGstPayableSlOptions);
    fetchSubAccountsByGL(d.rcm_recievable_gl, setRcmReceivableSlOptions);
    fetchSubAccountsByGL(d.rcm_payable_gl, setRcmPayableSlOptions);
    fetchSubAccountsByGL(
      d.gst_provisinal_recievable_gl,
      setGstProvisionalReceivableSlOptions
    );
    fetchSubAccountsByGL(d.itc_no_gl, setItcNoSlOptions);

    // ✅ set form values
    setForm({
      code_type_id: d.code_type_id, // INT
      code_type: d.code_type_name, // STRING
      hsn_sac_code: d.hsn_sac_master_id,
      gst_type: d.gst_type_id,
      effective_date: d.effective_date ? d.effective_date.substring(0, 10) : "",

      gst_receivable_gl: d.gst_recievable_gl,
      gst_receivable_sl: d.gst_recievable_sl,

      gst_payable_gl: d.gst_payable_gl,
      gst_payable_sl: d.gst_payable_sl,

      rcm_receivable_gl: d.rcm_recievable_gl,
      rcm_receivable_sl: d.rcm_receivable_sl,

      rcm_payable_gl: d.rcm_payable_gl,
      rcm_payable_sl: d.rcm_payable_sl,

      gst_provisional_receivable_gl: d.gst_provisinal_recievable_gl,
      gst_provisional_receivable_sl: d.gst_provisinal_recievable_sl,

      itc_no_gl: d.itc_no_gl,
      itc_no_sl: d.itc_no_sl,
    });

    // prevent back-navigation reuse
    window.history.replaceState({}, document.title);
  }, [routeLocation.state]);

  const resetForm = () =>
    setForm({
      code_type_id: "",
      code_type: "",
      hsn_sac_code: "",
      gst_type: "",
      effective_date: "",

      gst_receivable_gl: "",
      gst_receivable_sl: "",
      gst_payable_gl: "",
      gst_payable_sl: "",

      rcm_receivable_gl: "",
      rcm_receivable_sl: "",
      rcm_payable_gl: "",
      rcm_payable_sl: "",

      gst_provisional_receivable_gl: "",
      gst_provisional_receivable_sl: "",

      itc_no_gl: "",
      itc_no_sl: "",
    });

  // ---------- effective date validation ----------
  const validateEffectiveDate = async () => {
    // Let required field validation handle empty
    if (!form.effective_date) return false;

    try {
      const resp = await axios.post(`${baseUrl}/finance/check_effective_date`, {
        flag: "ACM", // ACM = account_code_mapping
      });

      const serverDateStr = resp.data?.effective_date; // e.g., "2025-01-01"
      if (!serverDateStr) {
        // No previous records for ACM, nothing to compare
        return true;
      }

      const latestExisting = new Date(serverDateStr);
      const newDate = new Date(form.effective_date);

      if (newDate < latestExisting) {
        toast.error(
          `Effective Date cannot be earlier than the latest existing Effective Date (${serverDateStr}).`
        );
        return false;
      }

      return true;
    } catch (err) {
      console.error("Error validating effective date", err);
      toast.error("Unable to validate Effective Date");
      // Block save if validation fails
      return false;
    }
  };

  // ---------- submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.code_type) return toast.error("Code Type is required");
    if (!form.hsn_sac_code) return toast.error("HSN/SAC Code is required");
    if (!form.gst_type) return toast.error("GST Type is required");
    if (!form.effective_date) return toast.error("Effective Date is required");

    // 🔹 Validate against latest effective_date from DB
    const isValidDate = await validateEffectiveDate();
    if (!isValidDate) {
      return;
    }

    const payload = {
      account_code_mapping_id: editId || 0,
      code_type_id: form.code_type_id,
      hsn_sac_master_id: form.hsn_sac_code,
      gst_type_id: form.gst_type,
      effective_date: form.effective_date,

      gst_recievable_gl: form.gst_receivable_gl || null,
      gst_recievable_sl: form.gst_receivable_sl || null,
      gst_payable_gl: form.gst_payable_gl || null,
      gst_payable_sl: form.gst_payable_sl || null,

      rcm_recievable_gl: form.rcm_receivable_gl || null,
      rcm_recievable_sl: form.rcm_receivable_sl || null,
      rcm_payable_gl: form.rcm_payable_gl || null,
      rcm_payable_sl: form.rcm_payable_sl || null,

      gst_provisinal_recievable_gl:
        form.gst_provisional_receivable_gl || null,
      gst_provisinal_recievable_sl:
        form.gst_provisional_receivable_sl || null,

      itc_no_gl: form.itc_no_gl || null,
      itc_no_sl: form.itc_no_sl || null,

      created_by: userObj.username || "admin",
      action: editId ? "UPDATE" : "CREATE",
    };

    try {
      await axios.post(`${baseUrl}/account-code-mapping/`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(
        editId ? "Record updated successfully" : "Record added successfully"
      );

      setEditId(null);
      resetForm();
    } catch (error) {
      const status = error?.response?.status;

      if (status === 409)
        toast.error("Account Code Mapping already exists");
      else if (status === 400) toast.error("Invalid data submitted");
      else toast.error("Server error while saving data");
    }
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className="commonSectionTitle">
                    {editId
                      ? "Edit Mapping Account Code"
                      : "Add Mapping Account Code"}
                  </h6>
                </Col>
              </Row>

              <Row>
                {/* Code Type */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Code Type <span className="requiredStar">*</span>
                      </Form.Label>
                      <Select
                        inputId="code_type"
                        placeholder="Select Code Type"
                        isSearchable
                        isClearable
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        value={
                          form.code_type_id
                            ? codeTypeOptions.find(
                                (o) => o.value === form.code_type_id
                              )
                            : null
                        }
                        onChange={(opt) => {
                          setForm((f) => ({
                            ...f,
                            code_type_id: opt ? opt.value : "", // ✅ INT
                            code_type: opt ? opt.name : "", // ✅ STRING
                            hsn_sac_code: "",
                          }));

                          fetchHsnByCodeType(opt ? opt.name : ""); // API needs STRING
                        }}
                        options={codeTypeOptions}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* HSN/SAC Code */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        HSN/SAC Code <span className="requiredStar">*</span>
                      </Form.Label>
                      <Select
                        inputId="hsn_sac_code"
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={hsnOptions}
                        isClearable
                        isSearchable
                        placeholder="Select HSN/SAC Code"
                        value={
                          hsnOptions.find(
                            (o) => o.value === form.hsn_sac_code
                          ) || null
                        }
                        onChange={(opt) =>
                          setForm((p) => ({
                            ...p,
                            hsn_sac_code: opt ? opt.value : "",
                          }))
                        }
                        classNamePrefix="rs"
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* GST Type */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        GST Type <span className="requiredStar">*</span>
                      </Form.Label>
                      <Select
                        inputId="gst_type"
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={gstTypeOptions}
                        isClearable
                        isSearchable
                        placeholder="Select GST Type"
                        value={
                          gstTypeOptions.find(
                            (o) => o.value === form.gst_type
                          ) || null
                        }
                        onChange={(opt) =>
                          setForm((p) => ({
                            ...p,
                            gst_type: opt ? opt.value : "",
                          }))
                        }
                        classNamePrefix="rs"
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* Effective Date */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Effective Date{" "}
                        <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={form.effective_date}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            effective_date: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* GST Receivable GL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>GST Receivable GL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={groupaccountOptions}
                        isClearable
                        isSearchable
                        placeholder="Select"
                        filterOption={accountFilter}
                        value={
                          groupaccountOptions.find(
                            (o) => o.value === form.gst_receivable_gl
                          ) || null
                        }
                        onChange={(opt) => {
                          const glId = opt ? opt.value : "";

                          setForm((p) => ({
                            ...p,
                            gst_receivable_gl: glId,
                            gst_receivable_sl: "", // reset SL
                          }));

                          fetchSubAccountsByGL(
                            glId,
                            setGstReceivableSlOptions
                          );
                        }}
                        classNamePrefix="rs"
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* GST Receivable SL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>GST Receivable SL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={gstReceivableSlOptions}
                        isClearable
                        isSearchable
                        placeholder="Select"
                        value={
                          gstReceivableSlOptions.find(
                            (o) => o.value === form.gst_receivable_sl
                          ) || null
                        }
                        onChange={(opt) =>
                          setForm((p) => ({
                            ...p,
                            gst_receivable_sl: opt ? opt.value : "",
                          }))
                        }
                        isDisabled={!form.gst_receivable_gl}
                        classNamePrefix="rs"
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* GST Payable GL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>GST Payable GL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={groupaccountOptions}
                        isClearable
                        isSearchable
                        placeholder="Select"
                        value={
                          groupaccountOptions.find(
                            (o) => o.value === form.gst_payable_gl
                          ) || null
                        }
                        onChange={(opt) => {
                          const glId = opt ? opt.value : "";

                          setForm((p) => ({
                            ...p,
                            gst_payable_gl: glId,
                            gst_payable_sl: "",
                          }));

                          fetchSubAccountsByGL(
                            glId,
                            setGstPayableSlOptions
                          );
                        }}
                        classNamePrefix="rs"
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* GST Payable SL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>GST Payable SL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={gstPayableSlOptions}
                        isClearable
                        isSearchable
                        placeholder="Select"
                        value={
                          gstPayableSlOptions.find(
                            (o) => o.value === form.gst_payable_sl
                          ) || null
                        }
                        onChange={(opt) =>
                          setForm((p) => ({
                            ...p,
                            gst_payable_sl: opt ? opt.value : "",
                          }))
                        }
                        isDisabled={!form.gst_payable_gl}
                        classNamePrefix="rs"
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* RCM Receivable GL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>RCM Receivable GL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={groupaccountOptions}
                        isClearable
                        isSearchable
                        placeholder="Select"
                        filterOption={accountFilter}
                        value={
                          groupaccountOptions.find(
                            (o) => o.value === form.rcm_receivable_gl
                          ) || null
                        }
                        onChange={(opt) => {
                          const glId = opt ? opt.value : "";

                          setForm((p) => ({
                            ...p,
                            rcm_receivable_gl: glId,
                            rcm_receivable_sl: "",
                          }));

                          fetchSubAccountsByGL(
                            glId,
                            setRcmReceivableSlOptions
                          );
                        }}
                        classNamePrefix="rs"
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* RCM Receivable SL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>RCM Receivable SL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={rcmReceivableSlOptions}
                        value={
                          rcmReceivableSlOptions.find(
                            (o) => o.value === form.rcm_receivable_sl
                          ) || null
                        }
                        onChange={(opt) =>
                          setForm((p) => ({
                            ...p,
                            rcm_receivable_sl: opt ? opt.value : "",
                          }))
                        }
                        isDisabled={!form.rcm_receivable_gl}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* RCM Payable GL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>RCM Payable GL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={groupaccountOptions}
                        isClearable
                        isSearchable
                        placeholder="Select"
                        filterOption={accountFilter}
                        value={
                          groupaccountOptions.find(
                            (o) => o.value === form.rcm_payable_gl
                          ) || null
                        }
                        onChange={(opt) => {
                          const glId = opt ? opt.value : "";

                          setForm((p) => ({
                            ...p,
                            rcm_payable_gl: glId,
                            rcm_payable_sl: "",
                          }));

                          fetchSubAccountsByGL(
                            glId,
                            setRcmPayableSlOptions
                          );
                        }}
                        classNamePrefix="rs"
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* RCM Payable SL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>RCM Payable SL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={rcmPayableSlOptions}
                        value={
                          rcmPayableSlOptions.find(
                            (o) => o.value === form.rcm_payable_sl
                          ) || null
                        }
                        onChange={(opt) =>
                          setForm((p) => ({
                            ...p,
                            rcm_payable_sl: opt ? opt.value : "",
                          }))
                        }
                        isDisabled={!form.rcm_payable_gl}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* GST Provisional Receivable GL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>GST Provisional Receivable GL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={groupaccountOptions}
                        isClearable
                        isSearchable
                        placeholder="Select"
                        filterOption={accountFilter}
                        value={
                          groupaccountOptions.find(
                            (o) =>
                              o.value === form.gst_provisional_receivable_gl
                          ) || null
                        }
                        onChange={(opt) => {
                          const glId = opt ? opt.value : "";

                          setForm((p) => ({
                            ...p,
                            gst_provisional_receivable_gl: glId,
                            gst_provisional_receivable_sl: "",
                          }));

                          fetchSubAccountsByGL(
                            glId,
                            setGstProvisionalReceivableSlOptions
                          );
                        }}
                        classNamePrefix="rs"
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* GST Provisional Receivable SL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>GST Provisional Receivable SL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={gstProvisionalReceivableSlOptions}
                        value={
                          gstProvisionalReceivableSlOptions.find(
                            (o) =>
                              o.value === form.gst_provisional_receivable_sl
                          ) || null
                        }
                        onChange={(opt) =>
                          setForm((p) => ({
                            ...p,
                            gst_provisional_receivable_sl: opt
                              ? opt.value
                              : "",
                          }))
                        }
                        isDisabled={!form.gst_provisional_receivable_gl}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* ITC No GL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>ITC No GL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={groupaccountOptions}
                        isClearable
                        isSearchable
                        placeholder="Select"
                        filterOption={accountFilter}
                        value={
                          groupaccountOptions.find(
                            (o) => o.value === form.itc_no_gl
                          ) || null
                        }
                        onChange={(opt) => {
                          const glId = opt ? opt.value : "";

                          setForm((p) => ({
                            ...p,
                            itc_no_gl: glId,
                            itc_no_sl: "",
                          }));

                          fetchSubAccountsByGL(glId, setItcNoSlOptions);
                        }}
                        classNamePrefix="rs"
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* ITC No SL */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>ITC No SL</Form.Label>
                      <Select
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        options={itcNoSlOptions}
                        value={
                          itcNoSlOptions.find(
                            (o) => o.value === form.itc_no_sl
                          ) || null
                        }
                        onChange={(opt) =>
                          setForm((p) => ({
                            ...p,
                            itc_no_sl: opt ? opt.value : "",
                          }))
                        }
                        isDisabled={!form.itc_no_gl}
                      />
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              {/* buttons row — same as AddTDSMaster */}
              <div className="d-flex justify-content-between mt-3">
                <div>
                  <Button
                    type="button"
                    className="btn btn-info clearBtn"
                    onClick={resetForm}
                  >
                    Reset
                  </Button>
                </div>
                <div>
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-danger secondaryBtn"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="success"
                    className="btn btn-success primaryBtn"
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AddMapAccountCode;
